import { IconPause, IconPlay, IconTrash } from "./icons";
import { normalizeScanStatus } from "../api";
import { useI18n, type TranslationKey } from "../i18n";

export type Stage = { name: string; pct: number; done?: number; total?: number };

// Backend/getScanStages emit fixed English phase names; localize at render
// time (api.ts stays i18n-agnostic so the EN deploy is unaffected).
const PHASE_KEY: Record<string, TranslationKey> = {
  RECON: "scan.phaseRecon",
  MAP: "scan.phaseMap",
  ANALYZE: "scan.phaseAnalyze",
  EXPLOIT: "scan.phaseExploit",
  CHAIN: "scan.phaseChain",
  REPORT: "scan.phaseReport",
};

export function ScanBar({
  elapsed,
  status,
  stages,
  onToggle,
  onDelete,
}: {
  elapsed: string;
  status: string;
  stages: Stage[];
  onToggle?: () => void;
  onDelete?: () => void;
}) {
  const normalizedStatus = normalizeScanStatus(status);
  const canToggle =
    normalizedStatus === "running" ||
    normalizedStatus === "paused" ||
    normalizedStatus === "idle";
  const { t } = useI18n();
  return (
    <div className={`scan scan--${normalizedStatus}`}>
      <div className="scan__timer">
        <div className="scan__time">
          <h2>{elapsed}</h2>
          <span className="scan__time-label">{t("scan.elapsed")}</span>
        </div>
        <div className="scan__actions">
          <button
            type="button"
            onClick={onToggle}
            disabled={!canToggle || !onToggle}
            aria-label={normalizedStatus === "running" ? t("scan.pause") : t("scan.resume")}
            title={normalizedStatus === "running" ? t("scan.pauseScan") : t("scan.resumeScan")}
          >
            {normalizedStatus === "running" ? (
              <IconPause width={24} height={24} />
            ) : (
              <IconPlay width={24} height={24} />
            )}
          </button>
          <button
            type="button"
            onClick={onDelete}
            disabled={!onDelete}
            aria-label={t("scan.cancelScan")}
            title={t("scan.cancelScan")}
          >
            <IconTrash width={24} height={24} />
          </button>
        </div>
      </div>

      <div className="scan__divider" />

      <div className="scan__progress" data-tour="scan-stages">
        {stages.map((s) => {
          return (
            <div key={s.name} className="scan__progress-stage">
              <span>
                {PHASE_KEY[s.name] ? t(PHASE_KEY[s.name]) : s.name}&nbsp;&nbsp;{s.pct}%
              </span>
              <div className="progress-bar">
                {s.pct > 0 ? (
                  <div
                    className={`progress-bar__fill${s.pct >= 100 ? " progress-bar__fill--full" : ""}`}
                    style={{ width: `${s.pct}%` }}
                  />
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
